import {
  Box, Chip, Fade, Grid, IconButton, Paper,
  Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  ToggleButton, ToggleButtonGroup, Typography, useTheme,
} from '@mui/material';
import { BarChart } from '@mui/x-charts/BarChart';
import { axisClasses } from '@mui/x-charts';
import React, { FunctionComponent, useContext, useEffect, useState } from 'react';
import SurveyAdminService from '../../../../shared/services/survey/survey-admin.service';
import type { ISurveyAnalyticsDashboard, ISurveyAnalyticsSurveyEntry } from '../../../../shared/services/survey/survey.model';
import { ReportPeriodContext } from '../../../../context/report-periods/report-periods';
import { AnalyticMetric } from '../graphs/analytic-metric';
import { getMixedColorPalette } from '../../../../shared/utils/colors-utils';
import { useSkeletonDelay } from '../../../../hooks/use-skeleton-delay';
import { CloseOutlined, WarningAmberOutlined } from '@mui/icons-material';
import EmissionService from '../../../../shared/services/emission/emission.service';
import { IEmission } from '../../../../shared/services/emission/emission.model';
import { EmissionsTable } from '../../emissons-components/overview/emissions-table';

const SURVEY_DATA_TYPE_ID = '0912dbd5-17cb-42a2-a93a-3bff89485ccc';

const fmt = (kg: number) => (kg / 1000).toLocaleString(undefined, { maximumFractionDigits: 2 });
const fmtTco2e = (kg: number) => kg / 1000;

type ChartGroupBy = 'rule' | 'group' | 'category';

interface ChartRow {
  [key: string]: string | number | null | undefined;
  label: string;
  committed: number;
  extrapolated: number;
}

export const AnalyticsSurveysTab: FunctionComponent<{
  reportPeriodId: string;
  /** Owned by the page: the survey filter renders in the shared analytics toolbar row. */
  selectedSurveyId?: string;
  onSurveysLoaded?: (surveys: Array<{ id: string; name: string }>) => void;
}> = ({ reportPeriodId, selectedSurveyId = '', onSurveysLoaded }) => {
  const theme = useTheme();
  const colors = getMixedColorPalette(theme.palette.primary.main);
  const reportPeriodContext = useContext(ReportPeriodContext);
  const requiredReportPeriodId = reportPeriodId || reportPeriodContext?.lastSelectedReportId;

  const [data, setData] = useState<ISurveyAnalyticsDashboard | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const showSkeleton = useSkeletonDelay(isLoading, 250);

  const [groupBy, setGroupBy] = useState<ChartGroupBy>('rule');
  const [warningDismissed, setWarningDismissed] = useState(false);

  // Emissions table state
  const [emissionRows, setEmissionRows] = useState<IEmission[]>([]);
  const [emissionCount, setEmissionCount] = useState(0);
  const [emissionPage, setEmissionPage] = useState(0);
  const [emissionPerPage, setEmissionPerPage] = useState(10);
  const [emissionOrderBy, setEmissionOrderBy] = useState<string | undefined>();
  const [emissionOrder, setEmissionOrder] = useState<'asc' | 'desc' | undefined>();
  const [emissionLoading, setEmissionLoading] = useState(false);


  useEffect(() => {
    if (!requiredReportPeriodId) {
      setIsLoading(false);
      setData(null);
      onSurveysLoaded?.([]);
      return;
    }
    setIsLoading(true);
    setLoadError(null);
    const svc = SurveyAdminService.getInstance();
    svc.getSurveyAnalyticsDashboard(requiredReportPeriodId)
      .then((payload) => {
        setData(payload);
        onSurveysLoaded?.((payload?.surveys ?? []).map((s) => ({ id: s.id, name: s.name })));
      })
      .catch(() => {
        setLoadError('Unable to load survey analytics.');
        onSurveysLoaded?.([]);
      })
      .finally(() => setIsLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requiredReportPeriodId]);

  // Load emissions table whenever report period, survey selection or pagination changes
  useEffect(() => {
    if (!requiredReportPeriodId) return;
    setEmissionLoading(true);
    const emissionService = new EmissionService();
    emissionService.getAll(
      requiredReportPeriodId,
      emissionPage,
      emissionPerPage,
      emissionOrderBy,
      emissionOrder,
      `&dataTypeId=${SURVEY_DATA_TYPE_ID}`,
    )
      .then((res) => { setEmissionRows(res.rows); setEmissionCount(res.count); })
      .catch(() => { setEmissionRows([]); setEmissionCount(0); })
      .finally(() => setEmissionLoading(false));
  }, [requiredReportPeriodId, emissionPage, emissionPerPage, emissionOrderBy, emissionOrder]);

  const activeSurvey: ISurveyAnalyticsSurveyEntry | null = selectedSurveyId
    ? (data?.surveys.find((s) => s.id === selectedSurveyId) ?? null)
    : null;

  const totals = activeSurvey
    ? { ...activeSurvey.totals, committedSessionCount: activeSurvey.committedSessionCount }
    : {
        committedKgCo2e: data?.combined.committedKgCo2e ?? 0,
        extrapolatedKgCo2e: data?.combined.extrapolatedKgCo2e ?? 0,
        totalKgCo2e: data?.combined.totalKgCo2e ?? 0,
        committedSessionCount: data?.combined.committedSessionCount ?? 0,
      };

  // Build chart dataset based on groupBy toggle
  const chartDataset: ChartRow[] = React.useMemo(() => {
    const sourceRules = activeSurvey
      ? activeSurvey.rules
      : (data?.combined.rulesBreakdown ?? []);

    if (groupBy === 'rule') {
      return [...sourceRules]
        .sort((a, b) => b.totalKgCo2e - a.totalKgCo2e)
        .map((r) => ({
          label: r.ruleName,
          committed: fmtTco2e(r.committedKgCo2e),
          extrapolated: fmtTco2e(r.extrapolatedKgCo2e),
        }));
    }

    if (groupBy === 'category') {
      const agg = new Map<string, { label: string; committed: number; extrapolated: number }>();
      for (const r of sourceRules) {
        const key = r.categoryId ?? '__unknown__';
        const label = r.categoryName ?? 'Unknown category';
        const existing = agg.get(key) ?? { label, committed: 0, extrapolated: 0 };
        existing.committed += fmtTco2e(r.committedKgCo2e);
        existing.extrapolated += fmtTco2e(r.extrapolatedKgCo2e);
        agg.set(key, existing);
      }
      return [...agg.values()].sort((a, b) => (b.committed + b.extrapolated) - (a.committed + a.extrapolated));
    }

    // By group
    const agg = new Map<string, { label: string; committed: number; extrapolated: number }>();
    for (const r of sourceRules) {
      const key = r.groupId ?? '__unknown__';
      const label = r.groupTitle ?? 'Unknown group';
      const existing = agg.get(key) ?? { label, committed: 0, extrapolated: 0 };
      existing.committed += fmtTco2e(r.committedKgCo2e);
      existing.extrapolated += fmtTco2e(r.extrapolatedKgCo2e);
      agg.set(key, existing);
    }
    return [...agg.values()].sort((a, b) => (b.committed + b.extrapolated) - (a.committed + a.extrapolated));
  }, [data, activeSurvey, groupBy]);

  const hasExtrapolation = activeSurvey
    ? activeSurvey.extrapolation !== null
    : (data?.surveys ?? []).some((s) => s.extrapolation !== null);

  const hasHighExtrap = (data?.surveys ?? []).some(
    (s) => s.extrapolation && s.extrapolation.scaleFactor > 1.2,
  );

  const hasData = data !== null && (
    activeSurvey ? activeSurvey.committedSessionCount > 0 : data.combined.committedSessionCount > 0
  );

  const noDataState = !isLoading && !loadError && (data === null || data.surveys.length === 0 || data.combined.committedSessionCount === 0);

  const chartHeight = Math.max(200, chartDataset.length * 48 + 40);
  const longestLabel = chartDataset.reduce((max, r) => Math.max(max, r.label.length), 0);
  const chartLeftMargin = Math.max(140, Math.min(260, longestLabel * 7));

  return (
    <>
      {isLoading && (
        <Fade in={showSkeleton} unmountOnExit>
          <Typography variant='body2' color='text.secondary'>Loading survey analytics…</Typography>
        </Fade>
      )}
      {!isLoading && loadError && (
        <Typography variant='body2' color='error'>{loadError}</Typography>
      )}
      {!isLoading && !loadError && (
        <Fade in={!showSkeleton} unmountOnExit>
          <div>
            {noDataState && (
              <Typography variant='body2' color='text.secondary'>
                No surveys with committed data found for this report period.
              </Typography>
            )}

            {hasData && (
              <Grid container spacing={2}>
                {/* Metric cards */}
                <Grid item xl={3} lg={3} md={6} sm={12} xs={12} sx={{ display: 'flex', flexDirection: 'column' }}>
                  <AnalyticMetric
                    title='Committed responses'
                    tooltip='Number of survey response sessions committed to this report period.'
                    value={totals.committedSessionCount}
                    units='responses'
                    compact
                  />
                </Grid>
                <Grid item xl={3} lg={3} md={6} sm={12} xs={12} sx={{ display: 'flex', flexDirection: 'column' }}>
                  <AnalyticMetric
                    title='Committed emissions'
                    tooltip='Total emissions from committed survey responses.'
                    value={fmtTco2e(totals.committedKgCo2e)}
                    units='tCO₂e'
                    compact
                  />
                </Grid>
                <Grid item xl={3} lg={3} md={6} sm={12} xs={12} sx={{ display: 'flex', flexDirection: 'column' }}>
                  <AnalyticMetric
                    title='Extrapolated addition'
                    tooltip='Extra emissions added by active extrapolation(s).'
                    value={fmtTco2e(totals.extrapolatedKgCo2e)}
                    units='tCO₂e'
                    compact
                  />
                </Grid>
                <Grid item xl={3} lg={3} md={6} sm={12} xs={12} sx={{ display: 'flex', flexDirection: 'column' }}>
                  <AnalyticMetric
                    title='Total (committed + extrapolated)'
                    tooltip='Combined committed and extrapolated emissions.'
                    value={fmtTco2e(totals.totalKgCo2e)}
                    units='tCO₂e'
                    compact
                  />
                </Grid>

                {/* >20% extrapolation quality warning */}
                {hasHighExtrap && !warningDismissed && (
                  <Grid item xs={12}>
                    <Box sx={{ p: 1.5, border: '1px solid', borderColor: 'warning.main', borderRadius: 1, position: 'relative' }}>
                      <Stack direction='row' spacing={1} alignItems='flex-start'>
                        <WarningAmberOutlined sx={{ color: 'warning.main', mt: 0.1, flexShrink: 0 }} fontSize='small' />
                        <Box sx={{ pr: 3 }}>
                          <Typography variant='body2' fontWeight={600} sx={{ mb: 0.5 }}>Please note</Typography>
                          <Typography variant='body2' color='text.secondary'>
                            One or more surveys have been extrapolated by more than 20% beyond their dataset's range. This introduces uncertainty and generally produces lower quality data.
                          </Typography>
                        </Box>
                      </Stack>
                      <IconButton
                        size='small'
                        onClick={() => setWarningDismissed(true)}
                        sx={{ position: 'absolute', top: 6, right: 6 }}
                        aria-label='Dismiss warning'
                      >
                        <CloseOutlined fontSize='small' />
                      </IconButton>
                    </Box>
                  </Grid>
                )}

                {/* Bar chart — emissions breakdown */}
                {chartDataset.length > 0 && (
                  <Grid item xs={12} sx={{ display: 'flex', flexDirection: 'column' }}>
                    <Paper variant='outlined' sx={{ p: 2 }}>
                      <Stack direction='row' alignItems='center' justifyContent='space-between' flexWrap='wrap' gap={1} sx={{ mb: 1.5 }}>
                        <Typography variant='subtitle1'><b>Emissions breakdown (tCO₂e)</b></Typography>
                        <ToggleButtonGroup
                          value={groupBy}
                          exclusive
                          size='small'
                          onChange={(_, v) => { if (v) setGroupBy(v); }}
                        >
                          <ToggleButton value='rule' sx={{ px: 1.5, py: 0.5, textTransform: 'none', fontSize: '0.8rem' }}>By rule</ToggleButton>
                          <ToggleButton value='group' sx={{ px: 1.5, py: 0.5, textTransform: 'none', fontSize: '0.8rem' }}>By group</ToggleButton>
                          <ToggleButton value='category' sx={{ px: 1.5, py: 0.5, textTransform: 'none', fontSize: '0.8rem' }}>By category</ToggleButton>
                        </ToggleButtonGroup>
                      </Stack>
                      {/* Manual legend */}
                      <Stack direction='row' spacing={2} sx={{ mb: 1 }}>
                        {[
                          { label: 'Committed', color: colors[0] },
                          ...(hasExtrapolation ? [{ label: 'Extrapolated addition', color: colors[2] }] : []),
                        ].map((entry) => (
                          <Stack key={entry.label} direction='row' alignItems='center' spacing={0.75}>
                            <Box sx={{ width: 14, height: 14, borderRadius: '3px', bgcolor: entry.color, flexShrink: 0 }} />
                            <Typography variant='caption'>{entry.label}</Typography>
                          </Stack>
                        ))}
                      </Stack>
                      <Box sx={{ width: '100%', height: chartHeight }}>
                        <BarChart
                          dataset={chartDataset}
                          yAxis={[{
                            scaleType: 'band',
                            dataKey: 'label',
                            tickSize: 0,
                            tickLabelStyle: { fontSize: 12 },
                          }]}
                          xAxis={[{ disableLine: true, tickSize: 0 }]}
                          series={[
                            { dataKey: 'committed', label: 'Committed', stack: 'total', valueFormatter: (v) => `${v?.toFixed(2) ?? '0'} tCO₂e` },
                            ...(hasExtrapolation ? [{ dataKey: 'extrapolated', label: 'Extrapolated addition', stack: 'total', valueFormatter: (v: number | null) => `${v?.toFixed(2) ?? '0'} tCO₂e` }] : []),
                          ]}
                          layout='horizontal'
                          colors={hasExtrapolation ? [colors[0], colors[2]] : [colors[0]]}
                          height={chartHeight}
                          borderRadius={4}
                          margin={{ left: chartLeftMargin, right: 16, top: 4, bottom: 28 }}
                          grid={{ vertical: true }}
                          slotProps={{ legend: { hidden: true } }}
                          axisHighlight={{ y: 'none' }}
                          sx={() => ({
                            [`.${axisClasses.root}`]: {
                              [`.${axisClasses.tick}, .${axisClasses.line}`]: { stroke: 'currentColor', strokeWidth: 1 },
                              [`.${axisClasses.tickLabel}`]: { fill: 'currentColor' },
                            },
                          })}
                        />
                      </Box>
                    </Paper>
                  </Grid>
                )}

                {/* Per-survey table — only in "All surveys" view */}
                {!selectedSurveyId && data && data.surveys.length > 1 && (
                  <Grid item xs={12} sx={{ display: 'flex', flexDirection: 'column' }}>
                    <Typography variant='subtitle1' sx={{ mb: 1 }}><b>By survey</b></Typography>
                    <TableContainer component={Paper} variant='outlined' sx={{ overflowX: 'auto' }}>
                      <Table size='medium' sx={{ minWidth: 640 }}>
                        <TableHead>
                          <TableRow>
                            <TableCell sx={{ fontWeight: 600 }}>Survey</TableCell>
                            <TableCell align='right' sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>Committed responses</TableCell>
                            <TableCell align='right' sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>Committed (tCO₂e)</TableCell>
                            <TableCell align='right' sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>Extrapolated (tCO₂e)</TableCell>
                            <TableCell align='right' sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>Total (tCO₂e)</TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>Extrapolation</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {data.surveys.map((s) => (
                            <TableRow key={s.id}>
                              <TableCell>{s.name}</TableCell>
                              <TableCell align='right'>{s.committedSessionCount.toLocaleString()}</TableCell>
                              <TableCell align='right'>{fmt(s.totals.committedKgCo2e)}</TableCell>
                              <TableCell align='right'>{fmt(s.totals.extrapolatedKgCo2e)}</TableCell>
                              <TableCell align='right' sx={{ fontWeight: 600 }}>{fmt(s.totals.totalKgCo2e)}</TableCell>
                              <TableCell>
                                {s.extrapolation ? (
                                  <Chip
                                    label={`×${s.extrapolation.scaleFactor.toFixed(2)} (${s.extrapolation.actualResponseCount}→${s.extrapolation.targetResponseCount})`}
                                    color='warning'
                                    size='small'
                                    variant='outlined'
                                  />
                                ) : (
                                  <Typography variant='caption' color='text.disabled'>None</Typography>
                                )}
                              </TableCell>
                            </TableRow>
                          ))}
                          <TableRow sx={{ bgcolor: 'action.hover' }}>
                            <TableCell sx={{ fontWeight: 700 }}>Total</TableCell>
                            <TableCell align='right' sx={{ fontWeight: 700 }}>{data.combined.committedSessionCount.toLocaleString()}</TableCell>
                            <TableCell align='right' sx={{ fontWeight: 700 }}>{fmt(data.combined.committedKgCo2e)}</TableCell>
                            <TableCell align='right' sx={{ fontWeight: 700 }}>{fmt(data.combined.extrapolatedKgCo2e)}</TableCell>
                            <TableCell align='right' sx={{ fontWeight: 700 }}>{fmt(data.combined.totalKgCo2e)}</TableCell>
                            <TableCell />
                          </TableRow>
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Grid>
                )}

                {/* Emission records table */}
                <Grid item xs={12} sx={{ display: 'flex', flexDirection: 'column' }}>
                  <Typography variant='subtitle1' sx={{ mb: 1 }}><b>Emission Records</b></Typography>
                  <EmissionsTable
                    emissions={emissionRows}
                    inputPage={emissionPage}
                    setInputPage={setEmissionPage}
                    totalRecords={emissionCount}
                    perPage={emissionPerPage}
                    setPerPage={setEmissionPerPage}
                    setOrderBy={setEmissionOrderBy}
                    setOrder={setEmissionOrder}
                    loading={emissionLoading}
                    backLink={'/analytics/dashboard?tab=surveys'}
                    backText='Back to analytics'
                  />
                </Grid>
              </Grid>
            )}
          </div>
        </Fade>
      )}
    </>
  );
};
